import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import { Amplify, Auth } from 'aws-amplify';
import UtilityRoutes from './UtilityRoutes';
import UIProvider from './context/provider/UIProvider';
import UserProvider from './context/provider/UserProvider';
import GlobalErrorBoundary from '../common/components/ErrorBoundary/GlobalErrorBoundary';
import ResponsiveProvider from './context/provider/ResponsiveProvider';
import LibraryProvider from './context/provider/LibraryProvider';
import LoginPage from '../userservice/routes/LoginPage';
import LogoutPage from '../userservice/routes/LogoutPage';
import SignUpPage from '../userservice/routes/SignUpPage';
import ForgotPasswordPage from '../userservice/routes/ForgotPasswordPage';
import ResetPasswordPage from '../userservice/routes/ResetPasswordPage';
import VerifyEmailPage from '../userservice/routes/VerifyEmailPage';
import VerifyOTP from '../userservice/routes/OTP/VerifyOTP';
import SetupOTP from '../userservice/routes/OTP/SetupOTP';
import PublicPage from './components/PublicPage';
import SharedVideo from './routes/newVideo/SharedVideo';

import crisp from '../common/lib/crisp';
import './utils/i18n';
import configs from '../../conf';
const config = configs[process.env.NODE_ENV];
import crypto from 'crypto';

let user = localStorage.getItem('userDetails');

// Initialize AWS Amplify
if (user == null) {
  // User NOT logged in
} else {
  user = JSON.parse(user);
  Amplify.configure({
    Auth: {
      identityPoolId: user.identityPoolId,
      region: user.region,
      userPoolId: user.userPoolId,

      // Required if userPoolId is defined? https://github.com/aws-amplify/amplify-js/issues/267
      userPoolWebClientId: user.userPoolWebClientId,
    },
  });
  localStorage.setItem('configured', JSON.stringify(true));
}

import VideoPreferenceProvider from './context/provider/VideoPreferenceProvider';
import VideoTrimmingProvider from './context/provider/VideoTrimmingProvider';
import UninstallPage from '../userservice/routes/UninstallPage';
import LanguageProvider from './context/provider/LanguageProvider';
import IntroOutroProvider from './context/provider/IntroOutroProvider';
import LogoProvider from './context/provider/LogoProvider';
import ChaptersProvider from './context/provider/ChaptersProvider';
import DownloadCenter from './routes/DownloadCenter/DownloadCenter';
import MessageProvider from './context/provider/MessageProvider';
import BackgroundMusicProvider from './context/provider/BackgroundMusicProvider';
import VideoSpeedUpProvider from './context/provider/VideoSpeedUpProvider';
import CallToActionProvider from './context/provider/CallToActionProvider';
import ZoomProvider from './context/provider/ZoomProvider';
import AssistantPage from './routes/Assistant/AssistantPage';
import VoiceoverProvider from './context/provider/VoiceoverProvider';
import VideoProgressProvider from './context/provider/VideoProgressProvider';

const ProtectedRoutes = lazy(() => import('./ProtectedRoutes'));
const ErrorPage = lazy(() => import('../common/components/ErrorPage'));

const App = () => {
  console.log('App');

  const [isReady, setIsReady] = useState(true);
  const [viewTimeout, setViewTimeout] = useState(null);

  window.$crisp = [];
  window.CRISP_WEBSITE_ID = config.crispId;

  (function () {
    var d = document;
    var s = d.createElement('script');

    s.src = 'https://client.crisp.chat/l.js';
    s.async = 1;

    s.onload = function () {
      window.$crisp.push(['do', 'chat:hide']);
    };

    d.getElementsByTagName('head')[0].appendChild(s);
  })();

  useEffect(() => {
    if (config.gtmTrackingId) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.gtmTrackingId}`;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.gtmTrackingId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.appendChild(noscript);
    }
  }, []);

  useEffect(() => {
    document.fonts.load('32px Nunito Sans').then(() => {
      const time = setTimeout(() => {
        setIsReady(true);
      }, 50);
      setViewTimeout(time);
    });
  }, []);

  useEffect(() => {
    if (isReady) {
      clearTimeout(viewTimeout);
      setViewTimeout(null);
    }
  }, [isReady]);

  return (
    <BrowserRouter>
      <GlobalErrorBoundary>
        <Suspense fallback={null}>
          <ResponsiveProvider>
            <UIProvider>
              <UserProvider>
                <VideoPreferenceProvider>
                  <VideoTrimmingProvider>
                    <LibraryProvider>
                      <MessageProvider>
                        <LanguageProvider>
                          <BackgroundMusicProvider>
                            <VideoSpeedUpProvider>
                              <IntroOutroProvider>
                                <LogoProvider>
                                  <VideoProgressProvider>
                                    <ChaptersProvider>
                                      <CallToActionProvider>
                                        <ZoomProvider>
                                          <VoiceoverProvider>
                                            <Switch>
                                              {/* ERROR & FALLBACK ROUTES - !!!ORDER MATTER!!! */}
                                              <Route path="/eg/404" exact render={props => <ErrorPage {...props} />} />
                                              <Route path="/eg/uninstall" render={props => <UninstallPage {...props} />} />

                                              {/* UTILITY ROUTES - should not be dynamic import */}
                                              <Route path="/eg/u/*" component={UtilityRoutes} />

                                              {/* AUTHENTICATION ROUTES */}
                                              <Route path="/logout" render={props => <LogoutPage {...props} />} />
                                              <Route path="/login" render={props => <LoginPage isReady={isReady} {...props} />} />
                                              <Route path="/sign-up" render={props => <SignUpPage {...props} />} />
                                              <Route path="/forgot-password" render={props => <ForgotPasswordPage {...props} />} />
                                              <Route path="/reset-password" render={props => <ResetPasswordPage {...props} />} />
                                              <Route path="/OTP/setup" render={props => <SetupOTP {...props} />} />
                                              <Route path="/OTP/verify" render={props => <VerifyOTP {...props} />} />
                                              <Route path="/confirm-email" render={props => <VerifyEmailPage {...props} />} />

                                              <Route
                                                path="/eg/download-center"
                                                render={props => (
                                                  <Suspense fallback={null}>
                                                    <PublicPage>
                                                      <DownloadCenter {...props} />
                                                    </PublicPage>
                                                  </Suspense>
                                                )}
                                              />
                                              {/* SHARED VIDEO PAGE */}
                                              <Route
                                                path="/engage/:videoId"
                                                render={props => (
                                                  <Suspense fallback={null}>
                                                    <PublicPage>
                                                      <SharedVideo videoId={props.match.params.videoId} {...props} />
                                                    </PublicPage>
                                                  </Suspense>
                                                )}
                                              />
                                              <Route path="/assistant" render={props => <AssistantPage props={props} />} />
                                              {/* PROTECTED ROUTES - this routes should always be the last in order */}
                                              <Route
                                                path="/eg/*"
                                                render={props => (
                                                  <Suspense fallback={null}>
                                                    <ProtectedRoutes {...props} />
                                                  </Suspense>
                                                )}
                                              />
                                              <Route
                                                path="/engage/:token"
                                                render={() => {
                                                  const { pathname } = window.location;
                                                  window.location = pathname;
                                                  return null;
                                                }}
                                              />
                                              <Redirect to="/eg/404" />
                                            </Switch>
                                            <ToastContainer
                                              position="top-right"
                                              autoClose={3000}
                                              hideProgressBar
                                              newestOnTop
                                              closeOnClick
                                              rtl={false}
                                              pauseOnVisibilityChange
                                              draggable
                                              pauseOnHover
                                              transition={Slide}
                                            />
                                          </VoiceoverProvider>
                                        </ZoomProvider>
                                      </CallToActionProvider>
                                    </ChaptersProvider>
                                  </VideoProgressProvider>
                                </LogoProvider>
                              </IntroOutroProvider>
                            </VideoSpeedUpProvider>
                          </BackgroundMusicProvider>
                        </LanguageProvider>
                      </MessageProvider>
                    </LibraryProvider>
                  </VideoTrimmingProvider>
                </VideoPreferenceProvider>
              </UserProvider>
            </UIProvider>
          </ResponsiveProvider>
        </Suspense>
      </GlobalErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
